import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, ChevronLeft as BackIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

interface LotteryResult {
  draw_date: string
  draw_type: string
  numbers: number[]
  first_prize: number | null
  three_digit_front: number[] | null
  three_digit_last: number[] | null
  two_digit: number | null
}

function formatThaiDate(dateStr: string): string {
  const months = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
  const [y, m, d] = dateStr.split('-').map(Number)
  return `${d} ${months[m - 1]} ${y + 543}`
}

function PrizeRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-3 border-b"
         style={{ borderColor: 'var(--color-surface-2)' }}>
      <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <div className="flex gap-2">{children}</div>
    </div>
  )
}

function NumBox({ n, large }: { n: number | string; large?: boolean }) {
  return (
    <span className={`inline-flex items-center justify-center rounded-xl font-bold ${large ? 'text-3xl px-4 py-2' : 'text-lg w-12 h-10'}`}
          style={{ background: large ? 'var(--color-primary)' : 'var(--color-surface-2)', color: '#fff' }}>
      {typeof n === 'number' ? n.toString().padStart(large ? 6 : 2, '0') : n}
    </span>
  )
}

function GovernmentCard() {
  const [index, setIndex] = useState(0) // 0 = ล่าสุด

  // ดึง history ทั้งหมด
  const { data: historyData, isLoading } = useQuery({
    queryKey: ['lottery', 'government', 'history'],
    queryFn: () => api.lottery.history('government', 50) as Promise<LotteryResult[]>,
    staleTime: 1000 * 60 * 10,
  })

  const total = historyData?.length ?? 0
  const current = historyData?.[index] ?? null
  const isLatest = index === 0
  const isOldest = index === total - 1

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--color-surface)' }}>
      {/* Header */}
      <div className="px-4 py-3" style={{ background: 'var(--color-primary)' }}>
        <div className="flex items-center justify-between">
          <p className="font-bold text-white">🇹🇭 ผลสลากกินแบ่งรัฐบาล</p>
          {!isLatest && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}>
              ย้อนหลัง
            </span>
          )}
        </div>
        {current && (
          <p className="text-sm text-white/80 mt-0.5">
            งวดวันที่ {formatThaiDate(current.draw_date)}
          </p>
        )}
      </div>

      {/* Nav bar */}
      <div className="flex items-center justify-between px-4 py-2"
           style={{ background: 'var(--color-surface-2)' }}>
        <button
          onClick={() => setIndex(i => i + 1)}
          disabled={isOldest || isLoading}
          className="flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-lg disabled:opacity-30"
          style={{ color: 'var(--color-text-muted)' }}>
          <ChevronLeft size={16} />
          งวดก่อน
        </button>

        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {total > 0 ? `${index + 1} / ${total} งวด` : ''}
        </span>

        <button
          onClick={() => setIndex(i => i - 1)}
          disabled={isLatest || isLoading}
          className="flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-lg disabled:opacity-30"
          style={{ color: isLatest ? 'var(--color-text-muted)' : 'var(--color-primary)' }}>
          งวดถัดไป
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="px-4 pb-4">
        {isLoading && (
          <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-muted)' }}>
            กำลังโหลด...
          </p>
        )}

        {!isLoading && !current && (
          <p className="text-sm py-4 text-center" style={{ color: 'var(--color-danger)' }}>
            ไม่พบข้อมูล
          </p>
        )}

        {current && (
          <>
            {/* รางวัลที่ 1 */}
            {current.first_prize && (
              <div className="py-4 text-center border-b" style={{ borderColor: 'var(--color-surface-2)' }}>
                <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>
                  รางวัลที่ 1 • รางวัลละ 6,000,000 บาท
                </p>
                <NumBox n={current.first_prize} large />
              </div>
            )}

            {/* เลขหน้า 3 ตัว */}
            {current.three_digit_front && current.three_digit_front.length > 0 && (
              <PrizeRow label="เลขหน้า 3 ตัว • 4,000 บาท">
                {current.three_digit_front.map((n, i) => (
                  <span key={i} className="font-bold text-lg"
                        style={{ color: 'var(--color-text)' }}>
                    {n.toString().padStart(3, '0')}
                  </span>
                ))}
              </PrizeRow>
            )}

            {/* เลขท้าย 3 ตัว */}
            {current.three_digit_last && current.three_digit_last.length > 0 && (
              <PrizeRow label="เลขท้าย 3 ตัว • 4,000 บาท">
                {current.three_digit_last.map((n, i) => (
                  <span key={i} className="font-bold text-lg"
                        style={{ color: 'var(--color-text)' }}>
                    {n.toString().padStart(3, '0')}
                  </span>
                ))}
              </PrizeRow>
            )}

            {/* เลขท้าย 2 ตัว */}
            {current.two_digit !== null && (
              <PrizeRow label="เลขท้าย 2 ตัว • 2,000 บาท">
                <span className="font-bold text-xl" style={{ color: 'var(--color-gold)' }}>
                  {current.two_digit.toString().padStart(2, '0')}
                </span>
              </PrizeRow>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default function LotteryPage() {
  const navigate = useNavigate()

  return (
    <div className="flex flex-col min-h-dvh p-4" style={{ background: 'var(--color-bg)' }}>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/')} style={{ color: 'var(--color-text-muted)' }}>
          <BackIcon size={24} />
        </button>
        <h1 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>ผลหวยย้อนหลัง</h1>
      </div>

      <div className="flex flex-col gap-4">
        <GovernmentCard />
      </div>

      <p className="text-center text-xs mt-6 mb-3 px-4" style={{ color: 'var(--color-text-muted)' }}>
        ข้อมูลเพื่อการศึกษาเท่านั้น อัปเดตอัตโนมัติทุกงวด
      </p>
    </div>
  )
}
